export interface CsvColumn {
  /** Property key on each row holding the raw cell value. */
  readonly source: string;
  /** Header text to emit in the CSV (also the projected record's key). */
  readonly label: string;
  /** Optional per-column projection — e.g. format a time x value as an ISO date. */
  readonly transform?: (value: unknown) => unknown;
}

const NEEDS_QUOTING = /[",\n\r]/;

/**
 * Quote a CSV field per RFC 4180: any field containing `,`, `"`, `\n` or
 * `\r` is wrapped in double quotes and embedded `"` is escaped by doubling.
 */
function escapeCsvField(field: string): string {
  if (NEEDS_QUOTING.test(field)) {
    return `"${field.replace(/"/g, '""')}"`;
  }
  return field;
}

/** Nullish becomes the empty cell; everything else is stringified. */
function formatValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value);
}

/**
 * Project an array of synthetic-keyed rows onto a label-keyed CSV. Panels
 * keep their internal row shape (`row.x` / `row[s.id]` for cartesian,
 * `row['c0']` / `row['c1']` for table) and declare the source-key → label
 * mapping once, with optional per-column transforms for formatting (date,
 * percentage, …). Header order follows `columns`.
 *
 * Quoting follows RFC 4180; nullish values (raw or post-transform) become
 * the empty CSV cell.
 */
export function csvFromRows<RowType>(rows: readonly RowType[], columns: readonly CsvColumn[]): string {
  if (rows.length === 0) {
    return '';
  }
  const header = columns.map((c) => escapeCsvField(c.label)).join(',');
  const body = rows.map((row) =>
    columns
      .map((c) => {
        const raw = (row as Record<string, unknown>)[c.source];
        return escapeCsvField(formatValue(c.transform ? c.transform(raw) : raw));
      })
      .join(','),
  );
  return [header, ...body].join('\n');
}
